import { isMealBuilderIntent } from "./assistantRules";

const ARABIC_TEXT_PATTERN = /[\u0600-\u06ff]/;

const FOOD_FIELDS = [
  "id",
  "name",
  "name_en",
  "name_ar",
  "category",
  "serving_size",
  "potassium_mg",
  "phosphorus_mg",
  "sodium_mg",
  "protein_g",
];

const CONNECTOR_PATTERN = /\s*(?:,|&|\+|\band\b|\bwith\b|\bplus\b|مع|\sو\s)\s*/iu;

const KNOWN_MEAL_INGREDIENTS = [
  {
    mealPattern: /\btabbouleh\b|تبولة/iu,
    mealName: { en: "Lebanese tabbouleh", ar: "تبولة" },
    ingredients: [
      { term: "parsley", display: { en: "parsley", ar: "بقدونس" } },
      { term: "tomato", display: { en: "tomato", ar: "طماطم" } },
      { term: "bulgur", display: { en: "bulgur", ar: "برغل" } },
      { term: "lemon", display: { en: "lemon", ar: "ليمون" } },
      { term: "olive oil", display: { en: "olive oil", ar: "زيت زيتون" } },
    ],
  },
];

const FOOD_TERM_ALIASES = [
  { term: "white rice", aliases: ["white rice", "رز أبيض"] },
  { term: "brown rice", aliases: ["brown rice", "رز بني"] },
  { term: "rice", aliases: ["rice", "رز"] },
  { term: "chicken", aliases: ["grilled chicken", "chicken", "دجاج"] },
  { term: "cucumber", aliases: ["cucumber", "خيار"] },
  { term: "parsley", aliases: ["parsley", "بقدونس"] },
  { term: "tomato", aliases: ["tomato", "طماطم", "بندورة"] },
  { term: "bulgur", aliases: ["bulgur", "برغل"] },
  { term: "lemon", aliases: ["lemon", "ليمون"] },
  { term: "olive oil", aliases: ["olive oil", "زيت زيتون"] },
];

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u064b-\u065f\u0670]/g, "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

const compactFood = (food) =>
  FOOD_FIELDS.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(food || {}, key)) {
      acc[key] = food[key];
    }
    return acc;
  }, {});

const numberOrNull = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const createId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `ai-meal-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const getAiMealDraftStorageKey = ({ guestMode, userId }) => {
  if (guestMode) return "ddp-ai-meal-draft-guest";
  return userId ? `ddp-ai-meal-draft-${userId}` : null;
};

export const isMealBuilderQuestion = (question) => isMealBuilderIntent(question);

export const buildSafeFoodContext = (foods = []) =>
  foods.map((food) => {
    const safeFood = compactFood(food);
    return {
      ...safeFood,
      potassium_mg: hasValue(safeFood.potassium_mg) ? numberOrNull(safeFood.potassium_mg) : null,
      phosphorus_mg: hasValue(safeFood.phosphorus_mg) ? numberOrNull(safeFood.phosphorus_mg) : null,
      sodium_mg: hasValue(safeFood.sodium_mg) ? numberOrNull(safeFood.sodium_mg) : null,
      protein_g: hasValue(safeFood.protein_g) ? numberOrNull(safeFood.protein_g) : null,
    };
  });

const foodSearchText = (food) =>
  normalizeText([food.name, food.name_en, food.name_ar, food.category, food.serving_size].filter(Boolean).join(" "));

const foodNames = (food) =>
  [food.name, food.name_en, food.name_ar].map(normalizeText).filter(Boolean);

const getQuestionTerms = (question) => {
  const language = ARABIC_TEXT_PATTERN.test(question) ? "ar" : "en";
  const normalizedQuestion = normalizeText(question);
  const terms = new Map();
  const addTerm = (term, display = term) => {
    const normalizedTerm = normalizeText(term);
    const normalizedDisplay = normalizeText(display);
    if (!normalizedTerm || !normalizedDisplay) return;

    for (const [existingTerm, existing] of terms.entries()) {
      const normalizedExistingDisplay = normalizeText(existing.display);
      if (normalizedExistingDisplay === normalizedDisplay) return;
      if (normalizedExistingDisplay.includes(normalizedDisplay)) return;
      if (normalizedDisplay.includes(normalizedExistingDisplay)) return;
    }

    terms.set(normalizedTerm, { term: normalizedTerm, display });
  };

  FOOD_TERM_ALIASES.forEach(({ term, aliases }) => {
    const matchedAlias = aliases.find((alias) => normalizedQuestion.includes(normalizeText(alias)));
    if (matchedAlias) addTerm(term, matchedAlias);
  });

  String(question || "")
    .toLowerCase()
    .split(CONNECTOR_PATTERN)
    .map((part) =>
      normalizeText(part)
        .replace(/\b(i|ate|had|mixed|prepared|made|lebanese|meal|with|and|tabbouleh)\b/giu, "")
        .replace(/اكلت|أكلت|حضرت|حضّرت|خلطت|مع|تبولة/g, "")
        .trim()
    )
    .filter((part) => part.length >= 2)
    .forEach((part) => addTerm(part));

  KNOWN_MEAL_INGREDIENTS.forEach((meal) => {
    if (meal.mealPattern.test(question)) {
      meal.ingredients.forEach((ingredient) => addTerm(ingredient.term, ingredient.display[language]));
    }
  });

  return [...terms.values()].filter(({ term }) => term);
};

const inferMealName = (question) => {
  const language = ARABIC_TEXT_PATTERN.test(question) ? "ar" : "en";
  const knownMeal = KNOWN_MEAL_INGREDIENTS.find((meal) => meal.mealPattern.test(question));
  if (knownMeal) return knownMeal.mealName[language];

  const cleaned = String(question || "")
    .replace(/\b(i ate|i had|i mixed|i prepared|i made)\b/iu, "")
    .replace(/اكلت|أكلت|حضرت|حضّرت|خلطت/g, "")
    .trim();

  return cleaned || (language === "ar" ? "وجبة مقترحة" : "Suggested mixed meal");
};

const findQuantity = (question, terms) => {
  const normalizedQuestion = normalizeText(question);
  const numericMatch = terms
    .flatMap((term) => [term.term, term.display])
    .map(normalizeText)
    .filter(Boolean)
    .map((term) => {
      const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return (
        normalizedQuestion.match(new RegExp(`(?:^|\\s)(\\d+(?:\\.\\d+)?)\\s+(?:servings?\\s+|cups?\\s+|pieces?\\s+)?${escapedTerm}(?:\\s|$)`, "iu")) ||
        normalizedQuestion.match(new RegExp(`${escapedTerm}\\s*(?:x|×)\\s*(\\d+(?:\\.\\d+)?)`, "iu"))
      );
    })
    .find(Boolean);

  if (!numericMatch) return null;
  const quantity = Number(numericMatch[1]);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : null;
};

const hasName = (food, name) => foodNames(food).some((foodName) => foodName === normalizeText(name));
const hasText = (food, text) => foodSearchText(food).includes(normalizeText(text));

const foodMatchesTerm = (food, term) => {
  switch (term.term) {
    case "white rice":
      return hasText(food, "white rice") || hasText(food, "رز ابيض");
    case "brown rice":
      return hasText(food, "brown rice") || hasText(food, "رز بني");
    case "rice":
      return hasName(food, "rice") || hasText(food, "white rice") || hasText(food, "brown rice");
    case "chicken":
      return hasText(food, "chicken breast") || hasText(food, "chicken");
    case "cucumber":
      return hasText(food, "cucumber") || hasText(food, "خيار");
    case "tomato":
      return hasName(food, "tomato") || hasName(food, "طماطم") || hasName(food, "بندورة");
    case "olive oil":
      return hasText(food, "olive oil") || hasText(food, "زيت زيتون");
    default:
      return hasText(food, term.term);
  }
};

const uniqueFoods = (foods) => {
  const seen = new Set();
  return foods.filter((food) => {
    const key = String(food.id ?? food.name ?? "");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getFoodDisplayName = (food) => food.name || food.name_en || food.name_ar || "";

const getFoodTermCandidates = (foods, term) => uniqueFoods(foods.filter((food) => foodMatchesTerm(food, term)));

export const buildAiMealDraft = ({ question, foods = [], createdAt = new Date().toISOString() }) => {
  if (!isMealBuilderQuestion(question)) {
    return {
      isMealBuilderRequest: false,
      draft: null,
      context: null,
    };
  }

  const safeFoods = buildSafeFoodContext(foods);
  const terms = getQuestionTerms(question);
  const matchedFoods = [];
  const unmatchedIngredients = [];
  const ambiguousIngredients = [];
  const seenFoodIds = new Set();

  terms.forEach((term) => {
    const candidates = getFoodTermCandidates(safeFoods, term);

    if (candidates.length === 0) {
      unmatchedIngredients.push(term.display || term.term);
      return;
    }

    if (candidates.length > 1) {
      ambiguousIngredients.push({
        name: term.display || term.term,
        options: candidates.map(getFoodDisplayName).filter(Boolean),
      });
      return;
    }

    const food = candidates[0];
    const foodKey = String(food.id);
    if (!seenFoodIds.has(foodKey)) {
      seenFoodIds.add(foodKey);
      matchedFoods.push({ food, matches: [term] });
    }
  });

  const ingredients = matchedFoods.map(({ food, matches }) => {
    const quantity = findQuantity(question, matches) || 1;
    return {
      id: createId(),
      foodId: food.id,
      foodKey: food.id,
      name: food.name || food.name_en || "",
      name_ar: food.name_ar || "",
      category: food.category || "",
      serving_size: food.serving_size || "",
      quantity,
      needsQuantityReview: !findQuantity(question, matches),
      food,
    };
  });

  const draft = ingredients.length
    ? {
        mealName: inferMealName(question),
        ingredients,
        unmatchedIngredients: [...new Set(unmatchedIngredients)].slice(0, 12),
        ambiguousIngredients: ambiguousIngredients.slice(0, 8),
        needsQuantityReview:
          ambiguousIngredients.length > 0 ||
          unmatchedIngredients.length > 0 ||
          ingredients.some((ingredient) => ingredient.needsQuantityReview),
        createdAt,
      }
    : null;

  return {
    isMealBuilderRequest: true,
    draft,
    context: {
      mealName: inferMealName(question),
      matchedFoods: matchedFoods.map(({ food }) => food),
      unmatchedIngredients: [...new Set(unmatchedIngredients)].slice(0, 12),
      ambiguousIngredients: ambiguousIngredients.slice(0, 8),
      needsQuantityReview:
        !ingredients.length ||
        ambiguousIngredients.length > 0 ||
        unmatchedIngredients.length > 0 ||
        ingredients.some((ingredient) => ingredient.needsQuantityReview),
      safeFoodCount: safeFoods.length,
    },
  };
};
